import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ACCOMMODATIONS, IMAGES } from "@/lib/constants";
import { LiveListings } from "@/components/LiveListings";
import { useLiveAccommodations } from "@/hooks/useLiveAccommodations";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6 },
};

export default function AccommodationsPage() {
  const { accommodations } = useLiveAccommodations();
  return (
    <>
      <Helmet>
        <title>Accommodations | Texoma Resorts - Cabins, Glamping, Domes & Camping</title>
        <meta
          name="description"
          content="Explore our luxury cabin, glamping safari tents, geodesic domes, and camping sites near Lake Texoma. Find your perfect stay at Serenity Village."
        />
      </Helmet>

      <section className="relative h-[50vh] min-h-[400px] flex items-end">
        <div className="absolute inset-0">
          <img src={IMAGES.cabin2} alt="Accommodations at Texoma Resorts" className="w-full h-full object-cover" />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="caption-text text-white/60 mb-3">Our Stays</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-light text-white tracking-tight">
              Accommodations
            </h1>
          </motion.div>
        </div>
      </section>

      <section data-testid="live-listings-section" className="py-16 md:py-24 bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="mb-10">
            <p className="caption-text text-accent mb-3">Live From Our Booking System</p>
            <h2 className="font-heading text-3xl md:text-5xl font-light text-foreground tracking-tight mb-4">
              All Stays, Real Rates
            </h2>
            <p className="font-body text-base text-muted-foreground max-w-2xl leading-relaxed">
              Every unit below is synced live with our booking system — real photos,
              current starting rates, and instant booking.
            </p>
          </motion.div>
          <LiveListings />
        </div>
      </section>

      <section data-testid="accommodations-list" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p {...fadeUp} className="font-body text-base md:text-lg text-muted-foreground max-w-2xl mb-16 leading-relaxed">
            From our signature Bet Cabin to glamping safari tents, geodesic domes,
            and traditional camping sites, Serenity Village offers stays for every
            kind of traveler.
          </motion.p>

          <div className="space-y-16">
            {accommodations.map((acc, i) => (
              <motion.div key={acc.id} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}>
                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${i % 2 !== 0 ? 'lg:direction-rtl' : ''}`}>
                  <div className={i % 2 !== 0 ? 'lg:order-2' : ''}>
                    <Link to={acc.slug}>
                      <div className="img-hover-zoom rounded-lg overflow-hidden">
                        <img src={acc.image} alt={acc.name} className="w-full aspect-[4/3] object-cover" />
                      </div>
                    </Link>
                  </div>
                  <div className={i % 2 !== 0 ? 'lg:order-1' : ''}>
                    <Badge variant="secondary" className="mb-3 font-body text-xs">{acc.capacity}</Badge>
                    <h2 className="font-heading text-3xl md:text-4xl font-normal text-foreground mb-3">{acc.name}</h2>
                    <p className="font-body text-sm text-accent mb-2 italic">{acc.tagline}</p>
                    <p className="font-body text-base text-muted-foreground leading-relaxed mb-6">{acc.description}</p>
                    <div className="grid grid-cols-2 gap-2 mb-6">
                      {acc.amenities.map((a) => (
                        <div key={a} className="flex items-center gap-2">
                          <Check size={14} className="text-primary shrink-0" />
                          <span className="font-body text-sm text-foreground/80">{a}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2 mb-6">
                      {acc.highlights.map((h) => (
                        <Badge key={h} variant="outline" className="font-body text-xs">{h}</Badge>
                      ))}
                    </div>
                    <Link to={acc.slug}>
                      <Button
                        data-testid={`book-${acc.id}`}
                        className="bg-primary hover:bg-primary/90 text-white font-body rounded-full px-6"
                      >
                        {acc.id === "camping" ? "Reserve Your Campsite" : "Reserve This Stay"}
                        <ArrowRight size={16} className="ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-secondary/30">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-3xl md:text-5xl font-light text-foreground tracking-tight mb-4">
              Not Sure Which Stay Is Right?
            </h2>
            <p className="font-body text-base text-muted-foreground mb-8">
              Contact us and we will help you find the perfect accommodation for your group.
            </p>
            <Link to="/contact">
              <Button data-testid="accommodations-contact" variant="outline" className="font-body rounded-full px-8 border-primary text-primary hover:bg-primary hover:text-white">
                Contact Us <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
